import React, { useState, useEffect, useMemo } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, Grid, Input, Empty } from "antd";
import Icon from "../util-components/Icon";
import { useDispatch, useSelector } from "react-redux";
import { SIDE_NAV_LIGHT } from "constants/ThemeConstant";
import utils from "utils";
import { onMobileNavToggle } from "store/slices/themeSlice";
import { useGetProfile } from "UserProfileContext";

const { useBreakpoint } = Grid;

// =========================
// Helper: default open keys
// =========================
const setDefaultOpen = (key) => {
  let keyList = [];
  let keyString = "";
  if (key) {
    const arr = key.split("-");
    for (let index = 0; index < arr.length; index++) {
      const elm = arr[index];
      index === 0 ? (keyString = elm) : (keyString = `${keyString}-${elm}`);
      keyList.push(keyString);
    }
  }
  return keyList;
};

// =========================
// MenuItem
// =========================
const MenuItem = ({ title, icon, path }) => {
  const dispatch = useDispatch();
  const isMobile = !utils.getBreakPoint(useBreakpoint()).includes("lg");

  const closeMobileNav = () => {
    if (isMobile) {
      dispatch(onMobileNavToggle(false));
    }
  };

  return (
    <div className="d-flex">
      <Link onClick={closeMobileNav} to={path} style={{ width: "100%" }}>
        {icon && <Icon type={icon} />}
        <span
          style={{ textTransform: "uppercase", fontSize: "13px" }}
          className="text-white"
        >
          {title}
        </span>
      </Link>
    </div>
  );
};

// =========================
// Detect level dari bentuk "menu"
// - 'satker'  : array satker -> bidang -> subbid -> subsi
// - 'bidang'  : object  -> subbid -> subsi
// - 'subbid'  : object  -> subsi
// - 'subsi'   : object tanpa child
// =========================
const detectMenuLevel = (menu) => {
  if (Array.isArray(menu)) return "satker";
  if (menu && typeof menu === "object") {
    if (Array.isArray(menu.subbid)) return "bidang";
    if (Array.isArray(menu.subsi)) return "subbid";
    return "subsi";
  }
  return "satker"; // fallback
};

// =========================
// Path builder (normalisasi jenis)
// =========================
const normalizeJenis = (jenis) => {
  if (jenis === "subbidang") return "subbid";
  return jenis;
};

// Ambil base path: "/dashboard/data-pegawai"
const getBasePath = (pathname) => {
  const parts = pathname.split("/").filter(Boolean);
  const base = `/${parts.slice(0, 2).join("/")}`;
  return base || "/dashboard/data-pegawai";
};

const makePath = (basePath, jenis, id) => {
  const j = normalizeJenis(jenis);
  switch (j) {
    case "satker":
      return `${basePath}/satker/${id}`;
    case "bidang":
      return `${basePath}/bidang/${id}`;
    case "subbid":
      return `${basePath}/subbid/${id}`;
    case "subsi":
      return `${basePath}/subsi/${id}`;
    default:
      return basePath;
  }
};

// =========================
// Filter utilities
// - Mengembalikan node yang match beserta children yang match
// =========================
const matchText = (txt = "", term = "") =>
  String(txt).toLowerCase().includes(String(term).toLowerCase());

const filterSatkerTree = (satkers, term) => {
  if (!term) return satkers;
  return satkers
    .map((s) => {
      const bidang = (s.bidang || [])
        .map((b) => {
          const subbid = (b.subbid || [])
            .map((sb) => {
              const subsi = (sb.subsi || []).filter((ss) =>
                matchText(ss.name, term)
              );
              const sbMatch = matchText(sb.name, term) || subsi.length > 0;
              return sbMatch ? { ...sb, subsi } : null;
            })
            .filter(Boolean);

          const bMatch = matchText(b.name, term) || subbid.length > 0;
          return bMatch ? { ...b, subbid } : null;
        })
        .filter(Boolean);

      const sMatch =
        matchText(s.alias, term) ||
        matchText(s.name, term) ||
        bidang.length > 0;
      return sMatch ? { ...s, bidang } : null;
    })
    .filter(Boolean);
};

const filterBidangTree = (bidang, term) => {
  if (!term) return bidang;
  const subbid = (bidang.subbid || [])
    .map((sb) => {
      const subsi = (sb.subsi || []).filter((ss) => matchText(ss.name, term));
      const sbMatch = matchText(sb.name, term) || subsi.length > 0;
      return sbMatch ? { ...sb, subsi } : null;
    })
    .filter(Boolean);

  const bMatch = matchText(bidang.name, term) || subbid.length > 0;
  return bMatch ? { ...bidang, subbid } : null;
};

const filterSubbidTree = (subbid, term) => {
  if (!term) return subbid;
  const subsi = (subbid.subsi || []).filter((ss) => matchText(ss.name, term));
  const sbMatch = matchText(subbid.name, term) || subsi.length > 0;
  return sbMatch ? { ...subbid, subsi } : null;
};

// =========================
// Builders → AntD Menu items
// =========================
const buildSubsiItems = (basePath, subsi, navigate) => {
  const key = `${subsi.id}`;
  return [
    {
      key,
      label: (
        <MenuItem
          title={subsi.name}
          path={makePath(basePath, "subsi", subsi.id)}
          onTitleClick={() => navigate(makePath(basePath, "subsi", subsi.id))}
        />
      ),
    },
  ];
};

const buildSubbidItems = (basePath, subbid, navigate) => {
  const key = `${subbid.id}`;
  if (subbid?.subsi?.length > 0) {
    return [
      {
        key,
        label: (
          <MenuItem
            title={subbid.name}
            path={makePath(basePath, "subbid", subbid.id)}
            onTitleClick={() =>
              navigate(makePath(basePath, "subbid", subbid.id))
            }
          />
        ),
        children: subbid.subsi.map((ss) => ({
          key: `${key}-${ss.id}`,
          label: (
            <MenuItem
              title={ss.name}
              path={makePath(basePath, "subsi", ss.id)}
              onTitleClick={() => navigate(makePath(basePath, "subsi", ss.id))}
            />
          ),
        })),
      },
    ];
  }
  return [
    {
      key,
      label: (
        <MenuItem
          title={subbid.name}
          path={makePath(basePath, "subbid", subbid.id)}
          onTitleClick={() => navigate(makePath(basePath, "subbid", subbid.id))}
        />
      ),
    },
  ];
};

const buildBidangItems = (basePath, bidang, navigate) => {
  const key = `${bidang.id}`;
  const children =
    bidang?.subbid?.length > 0
      ? bidang.subbid.flatMap((sb) => buildSubbidItems(basePath, sb))
      : null;
  return [
    {
      key,
      label: (
        <MenuItem
          title={bidang.name}
          path={makePath(basePath, "bidang", bidang.id)}
          onTitleClick={() => navigate(makePath(basePath, "bidang", bidang.id))}
        />
      ),
      children,
    },
  ];
};

const buildSatkerItems = (basePath, satkers, navigate) => {
  return (satkers || []).map((s) => {
    const key = `${s.id}`;
    const children =
      s?.bidang?.length > 0
        ? s.bidang.flatMap((b) => buildBidangItems(basePath, b))
        : null;
    return {
      key,
      label: (
        <MenuItem
          title={s.alias || s.name}
          path={makePath(basePath, "satker", s.id)}
          onTitleClick={() => navigate(makePath(basePath, "satker", s.id))}
        />
      ),
      children,
    };
  });
};

// =========================
// Component
// =========================
const SideNavContent = (props) => {
  const dataProfile = useGetProfile();
  const navigate = useNavigate();
  const [menuRaw, setMenuRaw] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    if (dataProfile) {
      setMenuRaw(dataProfile?.menu ?? null);
    }
  }, [dataProfile]);

  const { routeInfo, hideGroupTitle, sideNavTheme = SIDE_NAV_LIGHT } = props;
  const location = useLocation();
  const currentPath = location.pathname;
  const basePath = useMemo(() => getBasePath(currentPath), [currentPath]);

  const level = useMemo(() => detectMenuLevel(menuRaw), [menuRaw]);

  // Normalisasi nama field agar builder pakai .name
  const normalizeNames = (node) => {
    if (!node || typeof node !== "object") return node;
    const copy = Array.isArray(node)
      ? node.map((x) => normalizeNames(x))
      : { ...node };

    const rename = (obj, from, to) => {
      if (obj && obj[from] !== undefined && obj[to] === undefined) {
        obj[to] = obj[from];
      }
    };

    const normNode = (obj) => {
      rename(obj, "nama", "name");
      rename(obj, "alias", "alias"); // keep alias
      if (Array.isArray(obj.bidang)) obj.bidang = obj.bidang.map(normNode);
      if (Array.isArray(obj.subbid)) obj.subbid = obj.subbid.map(normNode);
      if (Array.isArray(obj.subsi)) obj.subsi = obj.subsi.map(normNode);
      return obj;
    };

    return Array.isArray(copy) ? copy.map(normNode) : normNode(copy);
  };

  const normalizedMenu = useMemo(() => normalizeNames(menuRaw), [menuRaw]);

  // Filtering sesuai level
  const filteredData = useMemo(() => {
    const term = (searchTerm || "").trim();
    if (!normalizedMenu) return normalizedMenu;

    if (level === "satker") {
      return filterSatkerTree(
        Array.isArray(normalizedMenu) ? normalizedMenu : [],
        term
      );
    } else if (level === "bidang") {
      return filterBidangTree(normalizedMenu, term);
    } else if (level === "subbid") {
      return filterSubbidTree(normalizedMenu, term);
    } else if (level === "subsi") {
      // untuk subsi, filter by self
      if (!term) return normalizedMenu;
      return matchText(normalizedMenu?.name, term) ? normalizedMenu : null;
    }
    return normalizedMenu;
  }, [normalizedMenu, level, searchTerm]);

  // Build AntD Menu items
  const menuItems = useMemo(() => {
    if (!filteredData) return [];
    if (level === "satker") {
      return buildSatkerItems(basePath, filteredData, navigate);
    } else if (level === "bidang") {
      return buildBidangItems(basePath, filteredData, navigate);
    } else if (level === "subbid") {
      return buildSubbidItems(basePath, filteredData, navigate);
    } else if (level === "subsi") {
      return buildSubsiItems(basePath, filteredData, navigate);
    }
    return [];
  }, [filteredData, level, basePath]);

  // Placeholder cari dinamis
  const searchPlaceholder = useMemo(() => {
    switch (level) {
      case "satker":
        return "Cari Satker/Bidang";
      case "bidang":
        return "Cari Satker/Bidang";
      case "subbid":
        return "Cari Satker/Bidang";
      case "subsi":
        return "Cari Satker/Bidang";
      default:
        return "Cari Satker/Bidang";
    }
  }, [level]);

  const { sideNavTheme: themeProp = SIDE_NAV_LIGHT } = props;
  const { currentTheme } = useSelector((state) => state.theme);

  const [openKeys, setOpenKeys] = useState([]);

  // Dapatkan daftar semua root (satker)
  const rootSubmenuKeys = useMemo(() => {
    if (!filteredData || !Array.isArray(filteredData)) return [];
    return filteredData.map((s) => String(s.id)); // asumsi id satker unik
  }, [filteredData]);

  const onOpenChange = (keys) => {
    const latestOpenKey = keys.find((key) => openKeys.indexOf(key) === -1);

    // Jika key yang baru dibuka bukan root, biarkan semuanya terbuka
    if (!rootSubmenuKeys.includes(latestOpenKey)) {
      setOpenKeys(keys);
    } else {
      // Jika root, hanya satu yang boleh terbuka
      setOpenKeys(latestOpenKey ? [latestOpenKey] : []);
    }

    // === Navigasi otomatis (tetap sama seperti sebelumnya) ===
    const clickedKey = latestOpenKey;

    const findAndNavigate = (nodes, key, jenisParent = level) => {
      if (!nodes) return false;
      for (const node of nodes) {
        const jenisNode = node.bidang
          ? "satker"
          : node.subbid
          ? "bidang"
          : node.subsi
          ? "subbid"
          : "subsi";

        if (String(node.id) === String(key)) {
          navigate(makePath(basePath, jenisNode, node.id));
          return true;
        }

        if (node.bidang && findAndNavigate(node.bidang, key, "bidang"))
          return true;
        if (node.subbid && findAndNavigate(node.subbid, key, "subbid"))
          return true;
        if (node.subsi && findAndNavigate(node.subsi, key, "subsi"))
          return true;
      }
      return false;
    };

    if (clickedKey) {
      if (Array.isArray(filteredData)) {
        findAndNavigate(filteredData, clickedKey);
      } else {
        findAndNavigate([filteredData], clickedKey);
      }
    }
  };

  return (
    <div style={{ background: currentTheme === "light" && "#16183f" }}>
      <div className="py-2 px-3">
        <Input
          placeholder={searchPlaceholder}
          onChange={(e) => setSearchTerm(e.target.value)}
          value={searchTerm}
          className="w-100"
          allowClear
        />
      </div>

      {menuItems.length === 0 ? (
        <div style={{ padding: 12 }}>
          <Empty description="Data menu tidak ditemukan" />
        </div>
      ) : (
        <Menu
          mode="inline"
          theme={themeProp === SIDE_NAV_LIGHT ? "light" : "dark"}
          style={{ height: "100%", borderInlineEnd: 0 }}
          selectedKeys={[props.routeInfo?.key || "default-key"]}
          openKeys={
            openKeys.length ? openKeys : setDefaultOpen(props.routeInfo?.key)
          }
          onOpenChange={onOpenChange}
          className={props.hideGroupTitle ? "hide-group-title" : ""}
          items={menuItems}
        />
      )}
    </div>
  );
};

const SatkerMenuContent = (props) => {
  return <SideNavContent {...props} />;
};

export default SatkerMenuContent;
